import { extractErrorMessage, suggestionName } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import AIPowerSearch from "../components/AIPowerSearch";
import RiskInsightsDashboard from "../components/RiskInsightsDashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  AlertTriangle,
  Shield,
  Plus,
  Eye,
  Pencil,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Zap,
  Cloud,
  Cpu,
  Users,
  MoreHorizontal,
  TrendingUp,
  Activity,
  Grid3X3,
  Layers,
  FileText,
  Info,
  ChevronRight,
  Target,
  MapPin,
  Sparkles,
} from "lucide-react";

const THREAT_CATEGORIES = [
  { value: "environmental", label: "Environmental", icon: Cloud, color: "bg-blue-100 text-blue-700" },
  { value: "utilities", label: "Utilities", icon: Zap, color: "bg-amber-100 text-amber-700" },
  { value: "equipment", label: "Equipment", icon: Cpu, color: "bg-purple-100 text-purple-700" },
  { value: "deliberate", label: "Deliberate", icon: AlertTriangle, color: "bg-red-100 text-red-700" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "bg-slate-100 text-slate-700" },
];

const VULNERABILITY_LEVELS = [
  { value: "v1", label: "V1 - High Vulnerability", description: "Significant exposure, many attack vectors" },
  { value: "v2", label: "V2 - Medium Vulnerability", description: "Moderate exposure, some weaknesses" },
  { value: "v3", label: "V3 - Low Vulnerability", description: "Limited exposure, few weaknesses" },
];

const CONTROL_EFFECTIVENESS = [
  { value: "c1", label: "C1 - Weak/No Controls", description: "Minimal or no protective measures" },
  { value: "c2", label: "C2 - Moderate Controls", description: "Some protective measures in place" },
  { value: "c3", label: "C3 - Strong Controls", description: "Comprehensive protective measures" },
];

const TREATMENT_OPTIONS = [
  { value: "implement_controls", label: "Implement Additional Controls", description: "Add new controls to reduce risk" },
  { value: "accept_risk", label: "Accept Risk", description: "Accept the risk with justification" },
  { value: "transfer_risk", label: "Transfer Risk", description: "Transfer risk via insurance/contract" },
  { value: "avoid_risk", label: "Avoid Risk", description: "Eliminate the risk source entirely" },
];

const RISK_LEVEL_COLORS = {
  HIGH: "bg-red-100 text-red-700 border-red-200",
  MODERATE: "bg-amber-100 text-amber-700 border-amber-200",
  LOW: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ELIMINATED: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_LABELS = {
  identified: "Identified",
  requires_treatment: "Requires Treatment",
  treatment_planned: "Treatment Planned",
  treated: "Treated",
  accepted: "Accepted",
  monitoring: "Monitoring",
};

// AI Risk Analysis Component
function AIRiskAnalysis({ api }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const response = await api.post('/bia-engine/risk/analyze', {
        include_all_risks: includeAll,
        max_recommendations: 5
      });
      setAnalysis(response.data);
      toast.success('Risk analysis complete');
    } catch (error) {
      toast.error('Failed to run risk analysis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-sky-700" />
          AI-Powered Risk Analysis
        </CardTitle>
        <CardDescription>
          Get intelligent control recommendations for your unaddressed HIGH risks
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Controls */}
        <div className="flex items-center gap-4">
          <Button onClick={runAnalysis} disabled={loading} data-testid="run-analysis-btn">
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              className="rounded border-slate-300"
            />
            Include all risks (not just unaddressed HIGH)
          </label>
        </div>

        {/* Results */}
        {analysis && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">{analysis.message}</p>
              <p className="text-xs text-slate-500 mt-1">
                Analyzed {analysis.analyzed_count} risks • 
                {analysis.ai_generated ? ' AI-powered analysis' : ' Rule-based analysis'}
              </p>
            </div>

            {/* Quick Wins */}
            {analysis.quick_wins && analysis.quick_wins.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Quick Wins
                </h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.quick_wins.map((win, idx) => (
                    <Badge key={idx} variant="outline" className="bg-emerald-50">
                      {win}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Risk Recommendations */}
            {analysis.risk_recommendations && analysis.risk_recommendations.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-slate-700">Control Recommendations by Risk</h4>
                {analysis.risk_recommendations.map((risk, idx) => (
                  <Card key={idx} className={`border-l-4 ${
                    risk.risk_level === 'HIGH' ? 'border-l-red-500' :
                    risk.risk_level === 'MODERATE' ? 'border-l-amber-500' : 'border-l-emerald-500'
                  }`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h5 className="font-medium">{risk.threat}</h5>
                          <p className="text-xs text-slate-500">
                            Risk Rating: {risk.risk_rating} • Level: {risk.risk_level} • 
                            {risk.existing_controls_count} existing controls
                          </p>
                        </div>
                        <Badge variant={risk.risk_level === 'HIGH' ? 'destructive' : 'secondary'}>
                          {risk.risk_level}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2">
                        {risk.recommendations.map((rec, recIdx) => (
                          <div key={recIdx} className="flex items-start gap-3 p-2 bg-slate-50 rounded">
                            <ChevronRight className="w-4 h-4 text-sky-700 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{rec.control}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <span>~{rec.risk_reduction_pct}% risk reduction</span>
                                <Badge variant="outline" className="text-xs">
                                  {rec.priority} priority
                                </Badge>
                                <span>Complexity: {rec.complexity}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Strategic Initiatives */}
            {analysis.strategic_initiatives && analysis.strategic_initiatives.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-sky-700" />
                  Strategic Initiatives
                </h4>
                <ul className="space-y-1">
                  {analysis.strategic_initiatives.map((init, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-sky-700">•</span>
                      {init}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!analysis && !loading && (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
            <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">No analysis run yet</p>
            <p className="text-sm text-slate-400">Click &quot;Run Analysis&quot; to get AI-powered control recommendations</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper function to derive Asset Rating from RTO (BIA-derived criticality)
const rtoToAssetRating = (rtoHours) => {
  if (rtoHours <= 4) return 3;  // Criticality Tier 1 (Critical) -> Asset Rating 3
  if (rtoHours <= 24) return 2; // Criticality Tier 2 (Important) -> Asset Rating 2
  return 1;                      // Criticality Tier 3 (Standard) -> Asset Rating 1
};

const getAssetRatingLabel = (rating) => {
  switch (rating) {
    case 3: return "3 - High Critical (Criticality Tier 1)";
    case 2: return "2 - Medium Critical (Criticality Tier 2)";
    case 1: return "1 - Low Critical (Criticality Tier 3)";
    default: return `${rating} - Unknown`;
  }
};

export default function RiskAssessmentPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [threats, setThreats] = useState([]);
  const [allThreats, setAllThreats] = useState([]); // full library (38) for AI-accept matching + dropdown fallback
  const [aiSuggestionContext, setAiSuggestionContext] = useState(null); // AI risk text shown in dialog during accept
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [riskMatrix, setRiskMatrix] = useState(null);
  const [aiSearchResults, setAiSearchResults] = useState(null);

  // Part 3D live indicator — poll the last auto-recalc event; pulse the Risk
  // Dashboard tab + toast when residual risk recalculates (plan/exercise change).
  const [recalcPulse, setRecalcPulse] = useState(false);
  const lastRecalcAtRef = React.useRef(undefined);
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const res = await api.get("/risk-advanced/recalc-status");
        const at = res.data?.at;
        if (!active || !at) return;
        if (lastRecalcAtRef.current === undefined) {
          lastRecalcAtRef.current = at; // baseline — no toast on first load
        } else if (at !== lastRecalcAtRef.current) {
          lastRecalcAtRef.current = at;
          const detail = res.data?.detail || "recovery coverage changed";
          toast.success(`Residual risk updated — ${detail}`, { duration: 5000 });
          setRecalcPulse(true);
          setTimeout(() => active && setRecalcPulse(false), 6000);
        }
      } catch { /* ignore */ }
    };
    check();
    const onFocus = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    const poll = setInterval(check, 15000);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      clearInterval(poll);
    };
  }, [api]);

  // Dialog states
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [showTreatmentDialog, setShowTreatmentDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [saving, setSaving] = useState(false);
  // AI-accept "draft & link" flow state
  const riskQueueRef = useRef({ items: [], created: 0, resolve: null });
  const [aiAcceptActive, setAiAcceptActive] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    process_id: "",
    threat_id: "",
    asset_rating: 2,
    threat_rating: 2,
    vulnerability_level: "v2",
    control_effectiveness: "c2",
    existing_controls: [],
    is_spof: false,
    spof_description: "",
    notes: "",
  });

  const [treatmentData, setTreatmentData] = useState({
    treatment_option: "",
    treatment_description: "",
    new_controls: [],
    implementation_date: "",
    responsible_person: "",
    risk_acceptance_justification: "",
  });

  const [tempControl, setTempControl] = useState("");
  const [tempNewControl, setTempNewControl] = useState("");
  const [assessmentStep, setAssessmentStep] = useState(0);

  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "admin";

  const [threatProfiles, setThreatProfiles] = useState({});
  const [filteredThreats, setFilteredThreats] = useState([]); // Threats filtered by asset types
  const [selectedProcessAssetTypes, setSelectedProcessAssetTypes] = useState([]); // Asset types from selected process
  // V22.9.8 — Auto-load Security & Privacy template hint when the selected
  // process's linked Application Profile carries high data_classification.
  const [securityHints, setSecurityHints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function loadHints() {
      if (!formData.process_id) { setSecurityHints([]); return; }
      const proc = processes.find((p) => p.id === formData.process_id);
      const appIds = proc?.application_dependencies || [];
      if (appIds.length === 0) { setSecurityHints([]); return; }
      try {
        const responses = await Promise.all(
          appIds.map((id) =>
            api.get("/asset-profiles/risk-template-hint", { params: { application_id: id } }).catch(() => null),
          ),
        );
        if (cancelled) return;
        const hits = responses
          .map((r) => r?.data)
          .filter((d) => d && d.high_sensitivity);
        setSecurityHints(hits);
      } catch { /* silent */ }
    }
    loadHints();
    return () => { cancelled = true; };
  }, [formData.process_id, processes, api]);
  
  // Location state for Task 1: Asset-Threat Alignment
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationThreatRatings, setLocationThreatRatings] = useState({});

  // Fetch threats filtered by asset types when process changes
  const fetchFilteredThreats = useCallback(async (assetTypes) => {
    const fallback = () => (allThreats.length ? allThreats : threats);
    if (!assetTypes || assetTypes.length === 0) {
      setFilteredThreats(fallback()); // Show all threats if no asset types selected
      return;
    }
    
    try {
      const assetTypesParam = assetTypes.join(",");
      const response = await api.get(`/threat-profiles/applicable?asset_types=${assetTypesParam}`);
      const applicableThreats = response.data.applicable_threats || [];
      
      // Merge with full threat data
      const applicableThreatIds = new Set(applicableThreats.map(t => t.id));
      const filtered = threats.filter(t => applicableThreatIds.has(t.id)).map(threat => ({
        ...threat,
        admin_rating: applicableThreats.find(a => a.id === threat.id)?.threat_rating || threat.default_rating,
        is_applicable_to_asset: true
      }));
      
      // Never leave the dropdown empty — fall back to the full threat library so a
      // threat can always be selected for any BIA process / asset type.
      setFilteredThreats(filtered.length ? filtered : fallback());
    } catch (error) {
      console.error("Failed to fetch filtered threats:", error);
      setFilteredThreats(fallback());
    }
  }, [api, threats, allThreats]);

  const fetchThreats = useCallback(async () => {
    try {
      // Fetch applicable threats from the regional threat profiling
      const [threatsRes, profilesRes] = await Promise.all([
        api.get("/bia-engine/risk/threats"),
        api.get("/threat-profiles/applicable")
      ]);
      
      const allThreats = threatsRes.data.threats || [];
      setAllThreats(allThreats);
      const applicableThreats = profilesRes.data.applicable_threats || [];
      
      // Create a map of threat profiles with admin-defined ratings
      const profileMap = {};
      applicableThreats.forEach(t => {
        profileMap[t.id] = {
          threat_rating: t.threat_rating,
          vulnerability_occurrence: t.vulnerability_occurrence,
          vulnerability_impact: t.vulnerability_impact,
          affected_asset_types: t.affected_asset_types || []
        };
      });
      setThreatProfiles(profileMap);
      
      // Merge threats with profile data - only show applicable threats
      const applicableThreatIds = new Set(applicableThreats.map(t => t.id));
      const mergedThreats = allThreats.filter(t => applicableThreatIds.has(t.id)).map(threat => ({
        ...threat,
        // Use admin-defined rating (locked)
        default_rating: profileMap[threat.id]?.threat_rating || threat.default_rating,
        admin_rating: profileMap[threat.id]?.threat_rating || threat.default_rating,
        vulnerability_occurrence: profileMap[threat.id]?.vulnerability_occurrence || "medium",
        vulnerability_impact: profileMap[threat.id]?.vulnerability_impact || "",
        affected_asset_types: profileMap[threat.id]?.affected_asset_types || [],
        is_profiled: !!profileMap[threat.id]
      }));
      
      setThreats(mergedThreats);
      setFilteredThreats(mergedThreats); // Initialize filtered threats with all threats
    } catch (error) {
      console.error("Failed to fetch threats:", error);
      // Fallback to default threats if profile fetch fails
      try {
        const response = await api.get("/bia-engine/risk/threats");
        setThreats(response.data.threats || []);
        setFilteredThreats(response.data.threats || []);
      } catch (err) {
        console.error("Failed to fetch default threats:", err);
      }
    }
  }, [api]);

  const fetchAssessments = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/risk/assessments");
      setAssessments(response.data.assessments || []);
    } catch (error) {
      console.error("Failed to fetch assessments:", error);
    }
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/risk/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [api]);

  const fetchProcesses = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/processes");
      setProcesses(response.data.processes || []);
    } catch (error) {
      console.error("Failed to fetch processes:", error);
    }
  }, [api]);

  const fetchRiskMatrix = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/risk/matrix");
      setRiskMatrix(response.data);
    } catch (error) {
      console.error("Failed to fetch risk matrix:", error);
    }
  }, [api]);

  // Fetch locations for the organization
  const fetchLocations = useCallback(async () => {
    try {
      const response = await api.get("/bcm-admin/locations");
      // Endpoint returns a bare array; tolerate both shapes.
      setLocations(Array.isArray(response.data) ? response.data : (response.data.locations || []));
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchThreats(),
        fetchAssessments(),
        fetchStats(),
        fetchProcesses(),
        fetchRiskMatrix(),
        fetchLocations(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchThreats, fetchAssessments, fetchStats, fetchProcesses, fetchRiskMatrix, fetchLocations]);

  const calculatePreviewRisk = () => {
    const { asset_rating, threat_rating, vulnerability_level, control_effectiveness } = formData;
    
    // Calculate probability
    const vHigh = vulnerability_level === "v1";
    const vMedium = vulnerability_level === "v2";
    const cWeak = control_effectiveness === "c1";
    const cModerate = control_effectiveness === "c2";
    
    let probability;
    if (vHigh && cWeak) probability = 3;
    else if ((vHigh && cModerate) || (vMedium && cWeak)) probability = 2;
    else probability = 1;
    
    const risk = asset_rating * threat_rating * probability;
    const level = risk > 9 ? "HIGH" : risk >= 4 ? "MODERATE" : "LOW";
    
    return { probability, risk, level };
  };

  const resetForm = () => {
    setFormData({
      process_id: "",
      threat_id: "",
      asset_rating: 2,
      threat_rating: 2,
      vulnerability_level: "v2",
      control_effectiveness: "c2",
      existing_controls: [],
      notes: "",
    });
    setSelectedThreat(null);
    setTempControl("");
    setSelectedLocationId("");
    setAssessmentStep(0);
    setAiSuggestionContext(null);
  };

  const openAssessmentDialog = (threat = null) => {
    resetForm();
    if (threat) {
      setSelectedThreat(threat);
      setFormData(prev => ({
        ...prev,
        threat_id: threat.id,
        threat_rating: threat.default_rating,
      }));
    }
    setShowAssessmentDialog(true);
  };

  // ── AI-accept "draft & link": open the create dialog pre-filled from a suggestion.
  // Threat name is matched to the applicable library threat when possible; the BCM
  // Admin picks the BIA process and commits — the record is created as a DRAFT. ──
  const prefillFromSuggestion = (raw) => {
    resetForm();
    const o = raw && typeof raw === "object" ? raw : {};
    const riskText = String(o.risk || o.name || suggestionName(raw) || "");
    const lc = riskText.toLowerCase();
    const stop = new Set(["the","a","an","of","to","in","on","for","and","or","risk","risks","attack","attacks","due","from","system","systems","failure","issue","issues","potential","possible"]);
    const tokens = lc.split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !stop.has(w));
    // Match against the FULL threat library (not just the org's applicable subset) so
    // realistic AI risk descriptions map to a valid library threat when possible.
    const library = allThreats.length ? allThreats : threats;
    let match = library.find((t) => {
      const n = (t.name || "").toLowerCase();
      return n && (lc.includes(n) || n.includes(lc));
    });
    if (!match && tokens.length) {
      // token-overlap scoring: pick the library threat sharing the most keywords
      let best = null; let bestScore = 0;
      for (const t of library) {
        const nTokens = (t.name || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !stop.has(w));
        const score = nTokens.filter((w) => tokens.includes(w)).length;
        if (score > bestScore) { bestScore = score; best = t; }
      }
      if (bestScore >= 1) match = best;
    }
    const noteBits = [
      riskText && `AI suggestion: ${riskText}`,
      o.category && `Category: ${o.category}`,
      o.likelihood && `Likelihood: ${o.likelihood}`,
      o.impact && `Impact: ${o.impact}`,
      o.source && `Source: ${o.source}`,
    ].filter(Boolean);
    setAiSuggestionContext({
      risk: riskText,
      category: o.category || null,
      likelihood: o.likelihood || null,
      impact: o.impact || null,
      matched: match ? match.name : null,
    });
    if (match) setSelectedThreat(match);
    setFormData(prev => ({
      ...prev,
      threat_id: match ? match.id : "",
      threat_rating: match ? (match.default_rating || 2) : 2,
      notes: noteBits.join(". "),
    }));
    setShowAssessmentDialog(true);
  };

  const advanceRiskQueue = useCallback(() => {
    const q = riskQueueRef.current;
    if (!q.items.length) {
      setShowAssessmentDialog(false);
      setAiAcceptActive(false);
      setAiSuggestionContext(null);
      const { resolve, created } = q;
      riskQueueRef.current = { items: [], created: 0, resolve: null };
      if (resolve) resolve(created > 0 ? { created } : { created: 0, error: "No assessment created" });
      return;
    }
    prefillFromSuggestion(q.items[0]);
  }, [threats, allThreats]);

  const handleAcceptRiskSuggestions = useCallback((items) => new Promise((resolve) => {
    riskQueueRef.current = { items: [...(items || [])], created: 0, resolve };
    setAiAcceptActive(true);
    advanceRiskQueue();
  }), [advanceRiskQueue]);

  const finishRiskAccept = () => {
    // Called when the admin closes/cancels the dialog mid-queue.
    riskQueueRef.current.items = [];
    advanceRiskQueue();
  };

  const openTreatmentDialog = async (assessment) => {
    try {
      const response = await api.get(`/bia-engine/risk/assessments/${assessment.id}`);
      setSelectedAssessment(response.data);
      setTreatmentData({
        treatment_option: "",
        treatment_description: "",
        new_controls: [],
        implementation_date: "",
        responsible_person: "",
        risk_acceptance_justification: "",
      });
      setShowTreatmentDialog(true);
    } catch (error) {
      toast.error("Failed to load assessment");
    }
  };

  const openViewDialog = async (assessment) => {
    try {
      const response = await api.get(`/bia-engine/risk/assessments/${assessment.id}`);
      setSelectedAssessment(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error("Failed to load assessment");
    }
  };

  // ── Edit threat-wizard assessment (PUT /bia-engine/risk/assessments/{id}) ──
  const canEditAssessment = ["bcm_manager", "department_head", "admin", "it_drp_manager"].includes(user?.role);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editTempControl, setEditTempControl] = useState("");

  const openEditDialog = async (assessment) => {
    try {
      const response = await api.get(`/bia-engine/risk/assessments/${assessment.id}`);
      const a = response.data;
      const snap = {
        threat_id: a.threat_id || "",
        asset_rating: Number(a.asset_rating ?? 1),
        threat_rating: Number(a.threat_rating ?? 1),
        vulnerability_level: a.vulnerability_level || "v2",
        control_effectiveness: a.control_effectiveness || "c2",
        existing_controls: Array.isArray(a.existing_controls) ? a.existing_controls : [],
        notes: a.notes || "",
        is_spof: !!a.is_spof,
        spof_description: a.spof_description || "",
        source: a.source || "",
      };
      setEditOriginal({ id: a.id, asset_rating_source: a.asset_rating_source, ...snap });
      setEditForm(snap);
      setEditTempControl("");
      setShowEditDialog(true);
    } catch (e) {
      toast.error("Failed to load assessment for editing");
    }
  };

  const handleSaveEdit = async () => {
    if (!editOriginal || !editForm) return;
    const payload = {};
    const keys = ["threat_id", "asset_rating", "threat_rating", "vulnerability_level",
                  "control_effectiveness", "notes", "is_spof", "spof_description", "source", "existing_controls"];
    for (const k of keys) {
      if (JSON.stringify(editForm[k]) !== JSON.stringify(editOriginal[k])) payload[k] = editForm[k];
    }
    if (Object.keys(payload).length === 0) { toast.info("No changes to save"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/bia-engine/risk/assessments/${editOriginal.id}`, payload);
      toast.success("Risk assessment updated");
      setShowEditDialog(false);
      await Promise.all([fetchAssessments(), fetchStats()]);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to update assessment"));
    } finally {
      setSavingEdit(false);
    }
  };

  const addEditControl = () => {
    const v = editTempControl.trim();
    if (v && !editForm.existing_controls.includes(v)) {
      setEditForm(prev => ({ ...prev, existing_controls: [...prev.existing_controls, v] }));
      setEditTempControl("");
    }
  };
  const removeEditControl = (i) => setEditForm(prev => ({ ...prev, existing_controls: prev.existing_controls.filter((_, idx) => idx !== i) }));

  const addExistingControl = () => {
    if (tempControl.trim() && !formData.existing_controls.includes(tempControl.trim())) {
      setFormData(prev => ({
        ...prev,
        existing_controls: [...prev.existing_controls, tempControl.trim()]
      }));
      setTempControl("");
    }
  };

  const removeExistingControl = (index) => {
    setFormData(prev => ({
      ...prev,
      existing_controls: prev.existing_controls.filter((_, i) => i !== index)
    }));
  };

  const addNewControl = () => {
    if (tempNewControl.trim() && !treatmentData.new_controls.includes(tempNewControl.trim())) {
      setTreatmentData(prev => ({
        ...prev,
        new_controls: [...prev.new_controls, tempNewControl.trim()]
      }));
      setTempNewControl("");
    }
  };

  const removeNewControl = (index) => {
    setTreatmentData(prev => ({
      ...prev,
      new_controls: prev.new_controls.filter((_, i) => i !== index)
    }));
  };

  const handleCreateAssessment = async () => {
    if (!formData.process_id) {
      toast.error("Please select a BIA process - Risk Assessment must be linked to a process");
      return;
    }
    if (!selectedLocationId) {
      toast.error("Please select the asset/process location");
      return;
    }
    if (!formData.threat_id) {
      toast.error("Please select a threat");
      return;
    }

    setSaving(true);
    try {
      // Include location_id in the assessment data
      const assessmentData = {
        ...formData,
        location_id: selectedLocationId,
        location_name: locations.find(l => l.id === selectedLocationId)?.name || "",
        ...(aiAcceptActive ? { status: "draft", source: "ai_suggestion" } : {}),
      };
      await api.post("/bia-engine/risk/assessments", assessmentData);
      toast.success(aiAcceptActive ? "Draft risk assessment created!" : "Risk assessment created!");
      await Promise.all([fetchAssessments(), fetchStats(), fetchRiskMatrix()]);
      if (aiAcceptActive) {
        // advance the AI-accept queue (opens next suggestion or closes + resolves)
        riskQueueRef.current.created += 1;
        riskQueueRef.current.items = riskQueueRef.current.items.slice(1);
        setSaving(false);
        advanceRiskQueue();
        return;
      }
      setShowAssessmentDialog(false);
      resetForm();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to create assessment"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddTreatment = async () => {
    if (!treatmentData.treatment_option || !treatmentData.treatment_description) {
      toast.error("Please select a treatment option and provide description");
      return;
    }

    if (selectedAssessment?.risk_level === "HIGH" && 
        treatmentData.treatment_option === "accept_risk" &&
        treatmentData.risk_acceptance_justification.length < 100) {
      toast.error("High risk acceptance requires at least 100 characters justification");
      return;
    }

    setSaving(true);
    try {
      await api.post(`/bia-engine/risk/assessments/${selectedAssessment.id}/treatment`, treatmentData);
      toast.success("Treatment plan added!");
      setShowTreatmentDialog(false);
      await Promise.all([fetchAssessments(), fetchStats(), fetchRiskMatrix()]);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to add treatment"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this risk assessment?")) return;

    try {
      await api.delete(`/bia-engine/risk/assessments/${id}`);
      toast.success("Risk assessment deleted");
      await Promise.all([fetchAssessments(), fetchStats(), fetchRiskMatrix()]);
    } catch (error) {
      toast.error("Failed to delete assessment");
    }
  };

  const getCategoryConfig = (category) => {
    return THREAT_CATEGORIES.find(c => c.value === category) || THREAT_CATEGORIES[4];
  };

  const preview = calculatePreviewRisk();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="risk-assessment-loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="risk-assessment-page">
      <AISuggestionsBanner module="risk_assessment"
        onAccept={handleAcceptRiskSuggestions}
        existingKeys={assessments.map((a) => a.threat_name).filter(Boolean)}
        itemKeyOf={(raw) => (raw?.risk || raw?.name || String(raw ?? ""))} />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-blue-100 text-blue-700">PLAN Phase</Badge>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">ISO 22301:8.2.3</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Risk Assessment</h1>
          <p className="text-slate-600 mt-1">
            Identify and assess risks to prioritized activities with 38-threat library
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={stats.total_assessments}
              complete={(stats.total_assessments || 0) - (stats.requiring_treatment || 0)}
              label="Risks Assessed"
            />
          </div>
        </div>
        <Button onClick={() => openAssessmentDialog()} className="bg-sky-700 hover:bg-sky-800" data-testid="new-assessment-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Risk Assessment
        </Button>
      </div>

      {/* Risk Formula Preview — Mockup Widget */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="risk-formula-preview">
        <div className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
          <span className="w-[7px] h-[7px] rounded-full bg-sky-700" /> Risk Formula Preview
        </div>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <div className="text-center px-5 py-3 bg-blue-50 border border-blue-200 rounded-lg min-w-[100px]">
            <p className="text-[0.5625rem] font-bold text-blue-400 uppercase tracking-wider">Asset Rating</p>
            <p className="text-2xl font-extrabold text-blue-600 mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>A</p>
            <p className="text-[10px] text-slate-400">BIA-derived</p>
          </div>
          <span className="text-xl text-slate-400 font-light">&times;</span>
          <div className="text-center px-5 py-3 bg-amber-50 border border-amber-200 rounded-lg min-w-[100px]">
            <p className="text-[0.5625rem] font-bold text-amber-400 uppercase tracking-wider">Threat Rating</p>
            <p className="text-2xl font-extrabold text-amber-600 mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>T</p>
            <p className="text-[10px] text-slate-400">Admin-profiled</p>
          </div>
          <span className="text-xl text-slate-400 font-light">&times;</span>
          <div className="text-center px-5 py-3 bg-purple-50 border border-purple-200 rounded-lg min-w-[100px]">
            <p className="text-[0.5625rem] font-bold text-purple-400 uppercase tracking-wider">Probability</p>
            <p className="text-2xl font-extrabold text-purple-600 mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>P</p>
            <p className="text-[10px] text-slate-400">V &times; C factor</p>
          </div>
          <span className="text-xl text-slate-400 font-light">=</span>
          <div className="text-center px-5 py-3 bg-red-50 border border-red-200 rounded-lg min-w-[100px]">
            <p className="text-[0.5625rem] font-bold text-red-400 uppercase tracking-wider">Risk Rating</p>
            <p className="text-2xl font-extrabold text-red-600 mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>R</p>
            <p className="text-[10px] text-slate-400">A &times; T &times; P</p>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-3">Thresholds: <span className="text-emerald-600 font-semibold">LOW &le;6</span> &bull; <span className="text-amber-600 font-semibold">MODERATE 7-12</span> &bull; <span className="text-red-600 font-semibold">HIGH &ge;13</span></p>
      </div>

      {/* Stats Cards — Compact Mockup Style */}
      {stats && (
        <div className="grid md:grid-cols-5 gap-3">
          {[
            { label: "Total Risks", val: stats.total_assessments, Icon: Shield, color: "text-sky-700", border: "" },
            { label: "High Risk", val: stats.by_risk_level?.HIGH || 0, Icon: AlertTriangle, color: "text-red-500", border: (stats.by_risk_level?.HIGH > 0 ? "border-red-200" : "") },
            { label: "Moderate", val: stats.by_risk_level?.MODERATE || 0, Icon: Activity, color: "text-amber-500", border: "" },
            { label: "Low Risk", val: stats.by_risk_level?.LOW || 0, Icon: CheckCircle2, color: "text-emerald-500", border: "" },
            { label: "Needs Treatment", val: stats.requiring_treatment || 0, Icon: Zap, color: "text-purple-500", border: (stats.requiring_treatment > 0 ? "border-amber-200" : "") },
          ].map((m) => (
            <div key={m.label} className={`bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all ${m.border}`} data-testid={`stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">{m.label}</p>
                  <p className="text-2xl font-extrabold text-slate-800 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{m.val}</p>
                </div>
                <m.Icon className={`w-8 h-8 ${m.color} opacity-60`} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="dashboard" className="space-y-4"
        onValueChange={(v) => { if (v === "dashboard") setRecalcPulse(false); }}>
        <TabsList className="bg-white border">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-sky-50 relative" data-testid="risk-dashboard-tab">
            <Layers className="w-4 h-4 mr-2" />
            Risk Dashboard
            {recalcPulse && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-testid="risk-recalc-pulse">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="assessments" className="data-[state=active]:bg-sky-50">
            <FileText className="w-4 h-4 mr-2" />
            Assessments
          </TabsTrigger>
          <TabsTrigger value="threats" className="data-[state=active]:bg-sky-50">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Threat Library (38)
          </TabsTrigger>
          <TabsTrigger value="matrix" className="data-[state=active]:bg-sky-50">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Risk Matrix
          </TabsTrigger>
          <TabsTrigger value="ai-analysis" className="data-[state=active]:bg-sky-50" data-testid="ai-analysis-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            AI Analysis
          </TabsTrigger>
        </TabsList>

        {/* Risk Dashboard Tab (Inherent vs Residual) */}
        <TabsContent value="dashboard" data-testid="dashboard-tab">
          <RiskInsightsDashboard />
        </TabsContent>

        {/* Assessments Tab */}
        <TabsContent value="assessments" data-testid="assessments-tab">
          <div className="mb-4">
            <AIPowerSearch
              module="risks"
              onResults={(results) => setAiSearchResults(results)}
              onClear={() => setAiSearchResults(null)}
              placeholder='Try: "find high-risk cyber threats" or "show untreated risks"...'
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Risk Assessments</CardTitle>
              <CardDescription>
                Identified risks with ratings and treatment status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assessments.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No risk assessments created yet</p>
                  <Button variant="outline" onClick={() => openAssessmentDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Assessment
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Threat</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Risk Rating</TableHead>
                      <TableHead>Residual</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(aiSearchResults || assessments).map((assessment) => {
                      const catConfig = getCategoryConfig(assessment.threat_category);
                      return (
                        <TableRow key={assessment.id} data-testid={`assessment-row-${assessment.id}`}>
                          <TableCell className="font-medium">{assessment.threat_name}</TableCell>
                          <TableCell>
                            <Badge className={catConfig.color}>{catConfig.label}</Badge>
                          </TableCell>
                          <TableCell>{assessment.process_name || "-"}</TableCell>
                          <TableCell>
                            <Badge className={RISK_LEVEL_COLORS[assessment.risk_level]}>
                              {assessment.risk_rating} ({assessment.risk_level})
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {assessment.residual_risk_rating !== null ? (
                              <Badge className={RISK_LEVEL_COLORS[assessment.residual_risk_level]}>
                                {assessment.residual_risk_rating} ({assessment.residual_risk_level})
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {STATUS_LABELS[assessment.status] || assessment.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openViewDialog(assessment)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {canEditAssessment && assessment.threat_id && assessment.vulnerability_level && (
                                <Button variant="ghost" size="icon" className="text-sky-700" onClick={() => openEditDialog(assessment)} data-testid={`edit-assessment-${assessment.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {assessment.requires_treatment && assessment.status !== "treated" && isBCMAdmin && (
                                <Button variant="ghost" size="icon" className="text-amber-600" onClick={() => openTreatmentDialog(assessment)}>
                                  <Zap className="w-4 h-4" />
                                </Button>
                              )}
                              {isBCMAdmin && (
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteAssessment(assessment.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Threat Library Tab */}
        <TabsContent value="threats" data-testid="threats-tab">
          <div className="space-y-4">
            {THREAT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const categoryThreats = threats.filter(t => t.category === category.value);
              
              return (
                <Card key={category.value}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${category.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      {category.label} Threats ({categoryThreats.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryThreats.map((threat) => (
                        <div
                          key={threat.id}
                          className="p-3 border rounded-lg hover:border-sky-300 hover:bg-sky-50/50 cursor-pointer transition-colors"
                          onClick={() => openAssessmentDialog(threat)}
                          data-testid={`threat-${threat.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{threat.name}</span>
                            <Badge variant="outline" className="text-xs">
                              Rating: {threat.default_rating}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">{threat.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Risk Matrix Tab */}
        <TabsContent value="matrix" data-testid="matrix-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-slate-400" />
                Risk Matrix (Asset × Threat)
              </CardTitle>
              <CardDescription>
                Visual representation of risk distribution
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 border bg-slate-50"></th>
                      <th className="p-2 border bg-slate-50 text-center">Low Threat (1)</th>
                      <th className="p-2 border bg-slate-50 text-center">Medium Threat (2)</th>
                      <th className="p-2 border bg-slate-50 text-center">High Threat (3)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[3, 2, 1].map((asset) => (
                      <tr key={asset}>
                        <td className="p-2 border bg-slate-50 font-medium text-center">
                          {asset === 3 ? "High Asset (3)" : asset === 2 ? "Medium Asset (2)" : "Low Asset (1)"}
                        </td>
                        {[1, 2, 3].map((threat) => {
                          const key = `${asset}_${threat}`;
                          const cell = riskMatrix?.matrix?.[key];
                          const maxRisk = asset * threat * 3; // Max probability is 3
                          const bgColor = maxRisk > 9 ? "bg-red-50" : maxRisk >= 4 ? "bg-amber-50" : "bg-emerald-50";
                          
                          return (
                            <td key={key} className={`p-3 border ${bgColor} text-center`}>
                              <div className="font-bold text-lg">{cell?.count || 0}</div>
                              <div className="text-xs text-slate-500">risks</div>
                              {cell?.assessments?.slice(0, 2).map((a, i) => (
                                <Badge key={i} variant="outline" className="text-xs mt-1 mr-1">
                                  {a.threat_name.slice(0, 10)}...
                                </Badge>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border border-red-200 rounded" />
                  <span>High Risk Zone ({">"} 9)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-100 border border-amber-200 rounded" />
                  <span>Moderate Zone (4-9)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-100 border border-emerald-200 rounded" />
                  <span>Low Risk Zone ({"<"} 4)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Analysis Tab */}
        <TabsContent value="ai-analysis" data-testid="ai-analysis-tab-content">
          <AIRiskAnalysis api={api} />
        </TabsContent>
      </Tabs>

      {/* Create Assessment Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showAssessmentDialog}
        onOpenChange={(open) => { setShowAssessmentDialog(open); if (!open) { if (aiAcceptActive) finishRiskAccept(); else resetForm(); } }}
        title="New Risk Assessment"
        currentStep={assessmentStep}
        onStepChange={setAssessmentStep}
        onSubmit={handleCreateAssessment}
        onCancel={() => { if (aiAcceptActive) finishRiskAccept(); else setShowAssessmentDialog(false); }}
        submitLabel={saving ? "Creating..." : "Create Assessment"}
        submitDisabled={saving || !formData.threat_id}
        saving={saving}
        canAdvance={
          assessmentStep === 0 ? !!formData.process_id :
          assessmentStep === 1 ? !!selectedLocationId :
          assessmentStep === 2 ? !!formData.threat_id :
          true
        }
        steps={[
          { id: "process", title: "Process", subtitle: "Which business process are you assessing? This auto-derives the asset criticality and filters applicable threats.", isComplete: !!formData.process_id },
          { id: "location", title: "Location", subtitle: "Where does this asset or process operate? The location's regional threat profile will inform the assessment.", isComplete: !!selectedLocationId, isDisabled: !formData.process_id },
          { id: "threat", title: "Threat", subtitle: "What specific threat are you assessing against this process? Only threats applicable to the process's asset types are shown.", isComplete: !!formData.threat_id, isDisabled: !selectedLocationId },
          { id: "assess", title: "Vulnerability", subtitle: "How exposed is this process to the selected threat? Rate the vulnerability level and effectiveness of existing controls.", isComplete: !!formData.vulnerability_level && !!formData.control_effectiveness, isDisabled: !formData.threat_id },
          { id: "review", title: "Review", subtitle: "Review the calculated risk rating. Add any existing controls, flag single points of failure, and add notes before submitting.", isComplete: false, isDisabled: !formData.threat_id },
        ]}
      >
        {/* AI-accept context — keeps the suggested threat name visible across every step */}
        {aiAcceptActive && aiSuggestionContext && (
          <div className="mb-4 p-3 rounded-lg border border-sky-200 bg-sky-50" data-testid="ai-suggestion-context">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-sky-600" />
              <span className="text-xs font-semibold text-sky-700 uppercase tracking-wide">AI Suggested Threat</span>
            </div>
            <p className="text-sm font-medium text-slate-900" data-testid="ai-suggestion-threat-name">{aiSuggestionContext.risk}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {aiSuggestionContext.category && <Badge variant="outline" className="text-[10px]">Category: {aiSuggestionContext.category}</Badge>}
              {aiSuggestionContext.likelihood && <Badge variant="outline" className="text-[10px]">Likelihood: {aiSuggestionContext.likelihood}</Badge>}
              {aiSuggestionContext.impact && <Badge variant="outline" className="text-[10px]">Impact: {aiSuggestionContext.impact}</Badge>}
            </div>
            <p className="text-xs text-sky-700 mt-2">
              {aiSuggestionContext.matched
                ? `Pre-selected closest library threat: "${aiSuggestionContext.matched}". Pick the BIA process, then adjust the threat if needed.`
                : "Select the BIA process and the closest matching threat from the library below."}
            </p>
          </div>
        )}

        {/* ── Step 0: Select BIA Process ── */}
        {assessmentStep === 0 && (
          <div className="space-y-4" data-testid="step-process">
            <Label className="flex items-center gap-1 text-base font-semibold">
              <Target className="w-4 h-4" />
              Link to BIA Process
            </Label>
            <Select 
              value={formData.process_id || "none"} 
              onValueChange={(value) => {
                const selectedProcess = processes.find(p => p.id === value);
                if (selectedProcess) {
                  const derivedRating = rtoToAssetRating(selectedProcess.calculated_rto_hours);
                  setFormData(prev => ({ 
                    ...prev, 
                    process_id: value,
                    asset_rating: derivedRating,
                    // Preserve the AI-matched threat during the accept flow; only reset for manual creation.
                    ...(aiAcceptActive ? {} : { threat_id: "", threat_rating: 2 })
                  }));
                  const assetTypes = selectedProcess.asset_types || [];
                  setSelectedProcessAssetTypes(assetTypes);
                  fetchFilteredThreats(assetTypes);
                  if (!aiAcceptActive) setSelectedThreat(null);
                } else {
                  setFormData(prev => ({ ...prev, process_id: "" }));
                  setSelectedProcessAssetTypes([]);
                  setFilteredThreats(allThreats.length ? allThreats : threats);
                  if (!aiAcceptActive) setSelectedThreat(null);
                }
              }}
            >
              <SelectTrigger data-testid="risk-process-select" className={!formData.process_id ? "border-amber-400" : "border-emerald-400"}>
                <SelectValue placeholder="Select a BIA process..." />
              </SelectTrigger>
              <SelectContent>
                {processes.map((proc) => (
                  <SelectItem key={proc.id} value={proc.id}>
                    {proc.process_name} (RTO: {proc.calculated_rto_hours}h)
                    {proc.asset_types?.length > 0 && ` — ${proc.asset_types.length} asset type(s)`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {formData.process_id && (
              <div className="space-y-3 mt-4">
                {selectedProcessAssetTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs text-slate-500 mr-1">Asset Types:</span>
                    {selectedProcessAssetTypes.map(type => (
                      <Badge key={type} variant="outline" className="text-xs bg-sky-50">
                        {type.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${
                  formData.asset_rating === 3 ? 'bg-red-50 border-red-200' :
                  formData.asset_rating === 2 ? 'bg-amber-50 border-amber-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                    formData.asset_rating === 3 ? 'bg-red-500' :
                    formData.asset_rating === 2 ? 'bg-amber-500' :
                    'bg-green-500'
                  }`}>
                    {formData.asset_rating}
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      Asset Rating: {formData.asset_rating === 3 ? 'High Critical (Tier 1)' :
                       formData.asset_rating === 2 ? 'Medium Critical (Tier 2)' : 'Low Critical (Tier 3)'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Auto-derived from RTO: {processes.find(p => p.id === formData.process_id)?.calculated_rto_hours || 0}h
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 1: Select Location ── */}
        {assessmentStep === 1 && (
          <div className="space-y-4" data-testid="step-location">
            <Label className="flex items-center gap-1 text-base font-semibold">
              <MapPin className="w-4 h-4" />
              Asset / Process Location
            </Label>
            <Select 
              value={selectedLocationId || "none"} 
              onValueChange={(value) => {
                if (value === "none") { setSelectedLocationId(""); return; }
                setSelectedLocationId(value);
                const selectedLoc = locations.find(l => l.id === value);
                if (selectedLoc && selectedLoc.risk_analysis) {
                  setLocationThreatRatings(selectedLoc.risk_analysis);
                }
              }}
            >
              <SelectTrigger data-testid="risk-location-select" className={!selectedLocationId ? "border-amber-400" : "border-emerald-400"}>
                <SelectValue placeholder="Select asset location..." />
              </SelectTrigger>
              <SelectContent>
                {locations.filter((l) => l.status !== "retired").length === 0 ? (
                  <SelectItem value="none" disabled>No locations configured. Add in Organization Setup.</SelectItem>
                ) : (
                  locations.filter((l) => l.status !== "retired").map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {loc.name} - {loc.address}
                        {loc.type && <Badge variant="outline" className="text-xs ml-1">{loc.type}</Badge>}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedLocationId && locationThreatRatings?.overall_risk && (
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                <p className="text-xs font-medium text-blue-700">Location Risk Profile:</p>
                <p className="text-sm text-blue-600">Overall Risk: {locationThreatRatings.overall_risk}</p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Select Threat ── */}
        {assessmentStep === 2 && (
          <div className="space-y-4" data-testid="step-threat">
            {!selectedThreat ? (
              <>
                <Label className="flex items-center gap-1 text-base font-semibold">Select Threat</Label>
                {filteredThreats.length > 0 && (
                  <p className="text-xs text-sky-700">{filteredThreats.length} threats applicable to selected process</p>
                )}
                <Select 
                  value={formData.threat_id} 
                  onValueChange={(value) => {
                    const threat = filteredThreats.find(t => t.id === value) || threats.find(t => t.id === value);
                    setSelectedThreat(threat);
                    setFormData(prev => ({
                      ...prev,
                      threat_id: value,
                      threat_rating: threat?.admin_rating || threat?.default_rating || 2
                    }));
                  }}
                >
                  <SelectTrigger data-testid="risk-threat-select">
                    <SelectValue placeholder="Choose a threat..." />
                  </SelectTrigger>
                  <SelectContent>
                    {THREAT_CATEGORIES.map((cat) => {
                      const categoryThreats = filteredThreats.filter(t => t.category === cat.value);
                      if (categoryThreats.length === 0) return null;
                      return (
                        <React.Fragment key={cat.value}>
                          <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">
                            {cat.label} ({categoryThreats.length})
                          </div>
                          {categoryThreats.map((threat) => (
                            <SelectItem key={threat.id} value={threat.id}>
                              {threat.name} (Rating: {threat.admin_rating || threat.default_rating})
                              {threat.is_profiled && " ✓"}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {selectedProcessAssetTypes.length > 0 
                    ? `Showing threats applicable to: ${selectedProcessAssetTypes.join(', ').replace(/_/g, ' ')}`
                    : "Showing all applicable threats"
                  }
                </p>
              </>
            ) : (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{selectedThreat.name}</p>
                    <p className="text-sm text-slate-500">{selectedThreat.description}</p>
                    {selectedThreat.vulnerability_occurrence && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        Regional Occurrence: {selectedThreat.vulnerability_occurrence}
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedThreat(null); setFormData(prev => ({ ...prev, threat_id: "" })); }}>
                    Change
                  </Button>
                </div>
                {/* Threat Rating display */}
                <div className={`flex items-center gap-3 p-3 rounded-lg border mt-3 ${
                  formData.threat_rating === 3 ? 'bg-red-50 border-red-200' :
                  formData.threat_rating === 2 ? 'bg-amber-50 border-amber-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                    formData.threat_rating === 3 ? 'bg-red-500' :
                    formData.threat_rating === 2 ? 'bg-amber-500' :
                    'bg-green-500'
                  }`}>
                    {formData.threat_rating}
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {formData.threat_rating === 3 ? 'High' : formData.threat_rating === 2 ? 'Medium' : 'Low'} Threat Level
                    </p>
                    <p className="text-xs text-slate-500">Set by BCM Manager in Regional Threat Profile</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Vulnerability & Controls ── */}
        {assessmentStep === 3 && (
          <div className="space-y-5" data-testid="step-vulnerability">
            {formData.threat_id && threatProfiles[formData.threat_id]?.vulnerability_impact && (
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                <p className="text-xs font-medium text-blue-700 mb-1">Regional Vulnerability Impact (from Admin Profile):</p>
                <p className="text-sm text-blue-800">{threatProfiles[formData.threat_id].vulnerability_impact}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Vulnerability Level</Label>
              <div className="space-y-2">
                {VULNERABILITY_LEVELS.map((v) => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, vulnerability_level: v.value }))}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.vulnerability_level === v.value 
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-medium text-sm">{v.label}</p>
                    <p className="text-xs text-slate-500">{v.description}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Control Effectiveness</Label>
              <div className="space-y-2">
                {CONTROL_EFFECTIVENESS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, control_effectiveness: c.value }))}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.control_effectiveness === c.value 
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-medium text-sm">{c.label}</p>
                    <p className="text-xs text-slate-500">{c.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Review & Submit ── */}
        {assessmentStep === 4 && (
          <div className="space-y-5" data-testid="step-review">
            {/* V22.9.8 — Auto-loaded Security & Privacy template hint */}
            {securityHints.length > 0 && (
              <div className="rounded-lg border border-violet-300 bg-violet-50 p-3" data-testid="risk-security-privacy-hint">
                <div className="flex items-center gap-2 mb-1.5">
                  <Badge className="bg-violet-600 text-white text-[10px]">Auto-loaded · Security & Privacy</Badge>
                  <span className="text-[11px] text-violet-700">
                    Linked app has data_classification = <strong>{securityHints[0].data_classification}</strong>
                  </span>
                </div>
                <p className="text-[12px] text-violet-900 mb-2 leading-snug">
                  {securityHints[0].templates?.find((t) => t.id === "security_privacy_risks")?.reason}
                </p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {(securityHints[0].templates?.find((t) => t.id === "security_privacy_risks")?.starter_risks || []).map((r, idx) => (
                    <div key={idx} className="bg-white border border-violet-100 rounded px-2 py-1.5 text-[11px]" data-testid={`risk-security-starter-${idx}`}>
                      <span className="font-medium text-slate-800">{r.title}</span>
                      <Badge variant="outline" className="ml-1.5 text-[9px] bg-slate-50 text-slate-500">{r.category}</Badge>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-violet-500 mt-2 italic">
                  ISO 22301 §8.3 + ISO 27005 · Create separate risk assessments for any starter risks relevant to this process.
                </p>
              </div>
            )}
            {/* Risk Preview */}
            <div className={`p-4 rounded-lg border-2 ${RISK_LEVEL_COLORS[preview.level]}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Calculated Risk Rating</p>
                  <p className="text-sm opacity-75">
                    {formData.asset_rating} x {formData.threat_rating} x {preview.probability} = {preview.risk}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{preview.risk}</p>
                  <Badge className={RISK_LEVEL_COLORS[preview.level]}>{preview.level}</Badge>
                </div>
              </div>
              {preview.level === "HIGH" && (
                <p className="text-sm mt-2 font-medium">This risk will require treatment</p>
              )}
            </div>

            {/* Existing Controls */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Existing Controls</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add existing control..."
                  value={tempControl}
                  onChange={(e) => setTempControl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExistingControl())}
                />
                <Button type="button" variant="outline" onClick={addExistingControl} size="sm">Add</Button>
              </div>
              {formData.existing_controls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {formData.existing_controls.map((control, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      {control}
                      <button onClick={() => removeExistingControl(idx)} className="ml-1 hover:text-red-500">x</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* SPOF */}
            <div className="space-y-3 p-4 border-2 border-dashed rounded-lg border-amber-300 bg-amber-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <Label className="text-amber-800 font-semibold">Single Point of Failure (SPOF)</Label>
                  <Badge variant="outline" className="text-xs">ISO 8.2.3</Badge>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_spof: !prev.is_spof }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.is_spof ? 'bg-amber-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.is_spof ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <p className="text-sm text-amber-700">Mark if this threat affects a component with no backup or redundancy.</p>
              {formData.is_spof && (
                <div className="space-y-2">
                  <Label className="text-amber-800">SPOF Description</Label>
                  <Textarea
                    placeholder="Describe the single point of failure..."
                    value={formData.spof_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, spof_description: e.target.value }))}
                    rows={2}
                    className="bg-white"
                  />
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea
                placeholder="Additional notes or context..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="risk-notes-input"
              />
            </div>
          </div>
        )}
      </StepDialog>

      {/* Treatment Dialog */}
      {/* Edit Risk Assessment dialog (threat-wizard schema only) */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { if (!o) { setShowEditDialog(false); setEditForm(null); setEditOriginal(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-assessment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-sky-700" />
              Edit Risk Assessment
            </DialogTitle>
            <DialogDescription>
              Changing asset rating, threat rating, vulnerability, or control effectiveness recalculates the risk score.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Threat</Label>
                <Select value={editForm.threat_id} onValueChange={(v) => setEditForm({ ...editForm, threat_id: v })}>
                  <SelectTrigger data-testid="edit-assessment-threat"><SelectValue placeholder="Choose a threat..." /></SelectTrigger>
                  <SelectContent>
                    {(allThreats.length ? allThreats : threats).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>
                    Asset Rating (1-3){" "}
                    <span className="text-xs text-slate-400">
                      {editOriginal?.asset_rating_source === "bia_derived" ? "(BIA-derived)" : "(Manual)"}
                    </span>
                  </Label>
                  <Input type="number" min={1} max={3} value={editForm.asset_rating}
                    onChange={(e) => setEditForm({ ...editForm, asset_rating: Number(e.target.value) })}
                    data-testid="edit-assessment-asset-rating" />
                </div>
                <div className="space-y-1.5">
                  <Label>Threat Rating (1-3)</Label>
                  <Input type="number" min={1} max={3} value={editForm.threat_rating}
                    onChange={(e) => setEditForm({ ...editForm, threat_rating: Number(e.target.value) })}
                    data-testid="edit-assessment-threat-rating" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Vulnerability Level</Label>
                  <Select value={editForm.vulnerability_level} onValueChange={(v) => setEditForm({ ...editForm, vulnerability_level: v })}>
                    <SelectTrigger data-testid="edit-assessment-vuln"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VULNERABILITY_LEVELS.map((v) => (<SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Control Effectiveness</Label>
                  <Select value={editForm.control_effectiveness} onValueChange={(v) => setEditForm({ ...editForm, control_effectiveness: v })}>
                    <SelectTrigger data-testid="edit-assessment-ctrl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONTROL_EFFECTIVENESS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Existing Controls</Label>
                <div className="flex gap-2">
                  <Input value={editTempControl} onChange={(e) => setEditTempControl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEditControl(); } }}
                    placeholder="Add a control and press Enter" data-testid="edit-assessment-control-input" />
                  <Button type="button" variant="outline" onClick={addEditControl}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {editForm.existing_controls.map((c, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {c}
                      <button type="button" onClick={() => removeEditControl(i)} className="ml-1 text-slate-500 hover:text-red-500"><XCircle className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-assessment-notes" />
              </div>
              <div className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <Label className="mb-0">Single Point of Failure (SPOF)</Label>
                  <button type="button"
                    onClick={() => setEditForm({ ...editForm, is_spof: !editForm.is_spof })}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${editForm.is_spof ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}
                    data-testid="edit-assessment-spof-toggle">
                    {editForm.is_spof ? "Yes — SPOF" : "No"}
                  </button>
                </div>
                {editForm.is_spof && (
                  <Textarea rows={2} placeholder="Describe the SPOF impact and mitigation needs"
                    value={editForm.spof_description} onChange={(e) => setEditForm({ ...editForm, spof_description: e.target.value })}
                    data-testid="edit-assessment-spof-desc" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Input value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })} data-testid="edit-assessment-source" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditForm(null); setEditOriginal(null); }} disabled={savingEdit} data-testid="edit-assessment-cancel">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-sky-700 hover:bg-sky-800" data-testid="edit-assessment-save">
              {savingEdit ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTreatmentDialog} onOpenChange={setShowTreatmentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Risk Treatment</DialogTitle>
            <DialogDescription>
              {selectedAssessment?.threat?.name} - Risk Rating: {selectedAssessment?.risk_rating} ({selectedAssessment?.risk_level})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Treatment Option *</Label>
              <Select value={treatmentData.treatment_option} onValueChange={(v) => setTreatmentData(prev => ({ ...prev, treatment_option: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select treatment approach" />
                </SelectTrigger>
                <SelectContent>
                  {TREATMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <p className="font-medium">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Treatment Description *</Label>
              <Textarea
                placeholder="Describe the treatment plan..."
                value={treatmentData.treatment_description}
                onChange={(e) => setTreatmentData(prev => ({ ...prev, treatment_description: e.target.value }))}
                rows={3}
              />
            </div>

            {treatmentData.treatment_option === "implement_controls" && (
              <div className="space-y-2">
                <Label>New Controls to Implement</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add new control"
                    value={tempNewControl}
                    onChange={(e) => setTempNewControl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNewControl())}
                  />
                  <Button type="button" variant="outline" onClick={addNewControl}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {treatmentData.new_controls.map((control, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      {control}
                      <button onClick={() => removeNewControl(idx)} className="ml-1 hover:text-red-500">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedAssessment?.risk_level === "HIGH" && treatmentData.treatment_option === "accept_risk" && (
              <div className="space-y-2">
                <Label className="text-red-600">Risk Acceptance Justification * (min 100 chars)</Label>
                <Textarea
                  placeholder="Provide detailed justification for accepting this high risk..."
                  value={treatmentData.risk_acceptance_justification}
                  onChange={(e) => setTreatmentData(prev => ({ ...prev, risk_acceptance_justification: e.target.value }))}
                  rows={4}
                  className="border-red-200"
                />
                <p className="text-xs text-slate-500">
                  {treatmentData.risk_acceptance_justification.length}/100 characters
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Implementation Date</Label>
                <Input
                  type="date"
                  value={treatmentData.implementation_date}
                  onChange={(e) => setTreatmentData(prev => ({ ...prev, implementation_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible Person</Label>
                <Input
                  placeholder="Name"
                  value={treatmentData.responsible_person}
                  onChange={(e) => setTreatmentData(prev => ({ ...prev, responsible_person: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTreatmentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTreatment} disabled={saving} className="bg-sky-700 hover:bg-sky-800">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Apply Treatment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Assessment Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedAssessment?.threat?.name}</DialogTitle>
            <DialogDescription>{selectedAssessment?.threat?.description}</DialogDescription>
          </DialogHeader>

          {selectedAssessment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-slate-500">Original Risk</p>
                    <p className="text-2xl font-bold">{selectedAssessment.risk_rating}</p>
                    <Badge className={RISK_LEVEL_COLORS[selectedAssessment.risk_level]}>
                      {selectedAssessment.risk_level}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-slate-500">Residual Risk</p>
                    <p className="text-2xl font-bold">
                      {selectedAssessment.residual_risk_rating ?? "-"}
                    </p>
                    {selectedAssessment.residual_risk_level && (
                      <Badge className={RISK_LEVEL_COLORS[selectedAssessment.residual_risk_level]}>
                        {selectedAssessment.residual_risk_level}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Asset Rating:</span>
                  <span className="font-medium">{selectedAssessment.asset_rating}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Threat Rating:</span>
                  <span className="font-medium">{selectedAssessment.threat_rating}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Probability:</span>
                  <span className="font-medium">{selectedAssessment.probability_rating}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status:</span>
                  <Badge variant="outline">{STATUS_LABELS[selectedAssessment.status]}</Badge>
                </div>
              </div>

              {selectedAssessment.treatment && (
                <Card className="border-emerald-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-emerald-800">Treatment Applied</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Option:</strong> {selectedAssessment.treatment.treatment_option}</p>
                    <p><strong>Description:</strong> {selectedAssessment.treatment.treatment_description}</p>
                    {selectedAssessment.treatment.new_controls?.length > 0 && (
                      <div>
                        <strong>New Controls:</strong>
                        <ul className="list-disc list-inside ml-2">
                          {selectedAssessment.treatment.new_controls.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
